/** Ordered script descriptors from the published Webflow HTML. */
export type WebflowScriptAsset = {
  /** Absolute URL for external scripts. */
  src?: string;
  /** Inline script body. */
  content?: string;
  type?: string;
  async?: boolean;
  defer?: boolean;
  id?: string;
  crossOrigin?: string;
  integrity?: string;
  referrerPolicy?: string;
  nomodule?: boolean;
};

export type WebflowExtractedAssets = {
  stylesheetHrefs: string[];
  inlineStyles: string[];
  /** Ordered scripts (jQuery → webflow.js → IX2/custom embeds). Prefer this for injection. */
  scripts: WebflowScriptAsset[];
  /** Convenience mirrors of `scripts` for debugging / page-data inspection. */
  scriptSrcs: string[];
  inlineScripts: string[];
  /** Font / preconnect links that should stay as <link> tags */
  fontLinks: Array<{ rel: string; href: string; crossOrigin?: string }>;
};

export type WebflowExtractedPage = {
  contentHtml: string;
  assets: WebflowExtractedAssets;
  title: string | null;
  /** Classes from the upstream <body> (Webflow interactions / page styles). */
  bodyClassName: string | null;
  /** From upstream <html> — needed for Webflow IX2 / site runtime. */
  htmlAttributes: {
    'data-wf-domain'?: string;
    'data-wf-page'?: string;
    'data-wf-site'?: string;
  };
};

const CONTENT_ATTR = 'data-tradewing-content';
export const WEBFLOW_CONTENT_WRAPPER_CLASS = 'w-webflow-tradewing-content';

/**
 * Double-class form of the wrapper selector — higher specificity than Chakra
 * element preflight, while still matching a single class on the DOM node.
 */
export const WEBFLOW_CONTENT_WRAPPER_SELECTOR = `.${WEBFLOW_CONTENT_WRAPPER_CLASS}.${WEBFLOW_CONTENT_WRAPPER_CLASS}`;

const toAbsoluteUrl = (rawUrl: string, baseUrl: string): string => {
  if (
    !rawUrl ||
    rawUrl.startsWith('data:') ||
    rawUrl.startsWith('blob:') ||
    rawUrl.startsWith('#') ||
    rawUrl.startsWith('mailto:') ||
    rawUrl.startsWith('tel:')
  ) {
    return rawUrl;
  }

  try {
    return new URL(rawUrl, baseUrl).toString();
  } catch {
    return rawUrl;
  }
};

const rewriteUrlsInCssOrHtml = (text: string, baseUrl: string): string => {
  return text
    .replace(
      /\b(href|src|srcset|action)=(["'])([^"']+)\2/gi,
      (full, attr, quote, value) => {
        if (attr.toLowerCase() === 'srcset') {
          const rewritten = value
            .split(',')
            .map((part: string) => {
              const trimmed = part.trim();
              const [url, ...rest] = trimmed.split(/\s+/);
              const absolute = toAbsoluteUrl(url, baseUrl);
              return [absolute, ...rest].join(' ');
            })
            .join(', ');

          return `${attr}=${quote}${rewritten}${quote}`;
        }

        return `${attr}=${quote}${toAbsoluteUrl(value, baseUrl)}${quote}`;
      }
    )
    .replace(/url\((['"]?)([^'")]+)\1\)/gi, (full, quote, value) => {
      if (value.startsWith('data:')) {
        return full;
      }

      return `url(${quote}${toAbsoluteUrl(value, baseUrl)}${quote})`;
    });
};

/**
 * Rewrite asset URLs for HTML embedded on the Tradewing host.
 * - src / srcset / form action → absolute (CDN / publish origin)
 * - href to the Webflow publish host → path-only so nav stays on Tradewing
 * - site-relative href (/about) → left alone
 */
const rewriteHtmlContentUrls = (html: string, publishOrigin: string): string => {
  let publishHost = '';
  try {
    publishHost = new URL(publishOrigin).host;
  } catch {
    publishHost = '';
  }

  return html.replace(
    /\b(href|src|srcset|action)=(["'])([^"']+)\2/gi,
    (full, attr, quote, value) => {
      const attrName = attr.toLowerCase();

      if (attrName === 'srcset') {
        const rewritten = value
          .split(',')
          .map((part: string) => {
            const trimmed = part.trim();
            const [url, ...rest] = trimmed.split(/\s+/);
            const absolute = toAbsoluteUrl(url, publishOrigin);
            return [absolute, ...rest].join(' ');
          })
          .join(', ');
        return `${attr}=${quote}${rewritten}${quote}`;
      }

      if (attrName === 'href') {
        const trimmed = value.trim();
        if (
          !trimmed ||
          trimmed.startsWith('#') ||
          trimmed.startsWith('mailto:') ||
          trimmed.startsWith('tel:') ||
          trimmed.startsWith('javascript:')
        ) {
          return full;
        }

        // Keep on-site relative paths on the Tradewing host.
        if (trimmed.startsWith('/') || trimmed.startsWith('?')) {
          return full;
        }

        try {
          const absolute = new URL(trimmed, publishOrigin);
          if (publishHost && absolute.host === publishHost) {
            return `${attr}=${quote}${absolute.pathname}${absolute.search}${absolute.hash}${quote}`;
          }
          return `${attr}=${quote}${absolute.toString()}${quote}`;
        } catch {
          return full;
        }
      }

      return `${attr}=${quote}${toAbsoluteUrl(value, publishOrigin)}${quote}`;
    }
  );
};

/**
 * HTML width/height attributes lose to any author CSS (Chakra's `img { height: auto }`,
 * our earlier `width: unset`, Webflow `max-width: 100%`). Promote them to inline
 * styles so Webflow logo sizes (e.g. 50×50) actually stick.
 */
const promoteImgDimensionAttributes = (html: string): string =>
  html.replace(/<img\b([^>]*?)(\/?)>/gi, (full, rawAttrs: string, selfClose: string) => {
    const widthMatch = rawAttrs.match(/\bwidth\s*=\s*(["']?)(\d+)\1/i);
    const heightMatch = rawAttrs.match(/\bheight\s*=\s*(["']?)(\d+)\1/i);
    const width = widthMatch?.[2];
    const height = heightMatch?.[2];

    if (!width && !height) {
      return full;
    }

    const dimStyles = [
      width ? `width:${width}px` : null,
      height ? `height:${height}px` : null,
      'max-width:none',
      'max-height:none',
    ]
      .filter(Boolean)
      .join(';');

    if (/\bstyle\s*=/i.test(rawAttrs)) {
      const attrs = rawAttrs.replace(
        /\bstyle\s*=\s*(["'])(.*?)\1/i,
        (_m, quote: string, style: string) =>
          `style=${quote}${dimStyles};${style}${quote}`
      );
      return `<img${attrs}${selfClose}>`;
    }

    return `<img style="${dimStyles}"${rawAttrs}${selfClose}>`;
  });

const isAtRulePrelude = (selector: string): boolean =>
  /^\s*@/.test(selector);

const isKeyframesAtRule = (selector: string): boolean =>
  /^\s*@keyframes\b/i.test(selector);

const isKeyframesStep = (selector: string): boolean =>
  /^\s*(from|to|\d+(\.\d+)?%)\s*$/i.test(selector);

const remapRootSelector = (
  selector: string,
  wrapperSelector: string
): string => {
  let result = selector
    .replace(/(^|[\s>+~|,])html(?=[\s>+~|.#:[,]|$)/gi, `$1${wrapperSelector}`)
    .replace(/(^|[\s>+~|,])body(?=[\s>+~|.#:[,]|$)/gi, `$1${wrapperSelector}`)
    .replace(
      /(^|[\s>+~|,]):root(?=[\s>+~|.#:[,]|$)/gi,
      `$1${wrapperSelector}`
    );

  const escaped = wrapperSelector.replace(/\./g, '\\.');
  return result.replace(
    new RegExp(`${escaped}\\s+${escaped}`, 'g'),
    wrapperSelector
  );
};

const scopeSelectorList = (
  prelude: string,
  wrapperSelector: string
): string => {
  if (!prelude.trim() || isAtRulePrelude(prelude) || isKeyframesStep(prelude)) {
    return prelude;
  }

  return prelude
    .split(',')
    .map((part) => {
      const leadingWs = part.match(/^\s*/)?.[0] ?? '';
      const trailingWs = part.match(/\s*$/)?.[0] ?? '';
      const selector = part.trim();

      if (!selector || isKeyframesStep(selector)) {
        return part;
      }

      const remapped = remapRootSelector(selector, wrapperSelector).trim();

      if (
        remapped === wrapperSelector ||
        remapped.startsWith(`${wrapperSelector}.`) ||
        remapped.startsWith(`${wrapperSelector}:`) ||
        remapped.startsWith(`${wrapperSelector}[`) ||
        remapped.startsWith(`${wrapperSelector} `)
      ) {
        return `${leadingWs}${remapped}${trailingWs}`;
      }

      return `${leadingWs}${wrapperSelector} ${remapped}${trailingWs}`;
    })
    .join(',');
};

/**
 * Webflow (QRCA) sets `.navbar_dropdown-link:hover { padding-left: 8px }` while
 * ≥1440px resting uses `var(--_theme---space--m)` (16px) — hover shifts text
 * left. Remove horizontal padding from those hover/focus rules so inset stays
 * stable; background/color hover still applies.
 */
export const neutralizeDropdownHoverPaddingShift = (css: string): string =>
  css.replace(/([^{}]+)\{([^{}]*)\}/g, (full, selector: string, body: string) => {
    if (
      !/navbar_dropdown-link/i.test(selector) ||
      !/:(?:hover|focus|focus-visible)/i.test(selector)
    ) {
      return full;
    }
    const cleaned = body
      .replace(/padding-left\s*:\s*[^;]+;?/gi, '')
      .replace(/padding-right\s*:\s*[^;]+;?/gi, '')
      .replace(/^\s*;+/, '')
      .replace(/;{2,}/g, ';');
    return `${selector}{${cleaned}}`;
  });

/**
 * Prefix every style-rule selector with the wrapper so Webflow rules beat
 * Chakra's preflight (`h1,h2,… { font-size: inherit }`), which loads later
 * and would otherwise crush Webflow heading sizes.
 *
 * Walks CSS by brace-depth so declaration blocks are never treated as selectors.
 * Rules nested inside @media/@supports are scoped; @keyframes steps are not.
 */
export const scopeWebflowCss = (
  css: string,
  wrapperSelector = WEBFLOW_CONTENT_WRAPPER_SELECTOR
): string => {
  // Strip comments first so `{`/`}` inside them cannot confuse the walker,
  // and so comments never end up inside a scoped selector list.
  const source = css.replace(/\/\*[\s\S]*?\*\//g, '');

  let output = '';
  let depth = 0;
  /** Depth at which an open @keyframes block started (0 = none). */
  let keyframesDepth = 0;
  let chunkStart = 0;
  let i = 0;

  while (i < source.length) {
    // Skip strings inside declaration blocks.
    if ((source[i] === '"' || source[i] === "'") && depth > 0) {
      const quote = source[i];
      let j = i + 1;
      while (j < source.length) {
        if (source[j] === '\\') {
          j += 2;
          continue;
        }
        if (source[j] === quote) {
          j += 1;
          break;
        }
        j += 1;
      }
      i = j;
      continue;
    }

    if (source[i] === '{') {
      const prelude = source.slice(chunkStart, i);

      if (keyframesDepth > 0) {
        output += prelude;
      } else if (isKeyframesAtRule(prelude)) {
        output += prelude;
        keyframesDepth = depth + 1;
      } else if (isAtRulePrelude(prelude)) {
        output += prelude;
      } else {
        output += scopeSelectorList(prelude, wrapperSelector);
      }

      output += '{';
      depth += 1;
      chunkStart = i + 1;
      i += 1;
      continue;
    }

    if (source[i] === '}') {
      output += source.slice(chunkStart, i);
      output += '}';
      if (keyframesDepth > 0 && depth === keyframesDepth) {
        keyframesDepth = 0;
      }
      depth = Math.max(0, depth - 1);
      chunkStart = i + 1;
      i += 1;
      continue;
    }

    i += 1;
  }

  output += source.slice(chunkStart);
  return neutralizeDropdownHoverPaddingShift(output);
};

/** @deprecated Use scopeWebflowCss */
export const rewriteWebflowCssRootSelectors = scopeWebflowCss;

const extractByAttribute = (html: string): string | null => {
  const openTagMatch =
    html.match(
      new RegExp(
        `<([a-zA-Z][\\w:-]*)([^>]*\\s${CONTENT_ATTR}(?:\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s>]*))?[^>]*)>`,
        'i'
      )
    ) ||
    html.match(
      new RegExp(
        `<([a-zA-Z][\\w:-]*)([^>]*\\sclass\\s*=\\s*(["'])(?:(?!\\3).)*\\b${CONTENT_ATTR}\\b(?:(?!\\3).)*\\3[^>]*)>`,
        'i'
      )
    );

  if (!openTagMatch || openTagMatch.index === undefined) {
    return null;
  }

  const tagName = openTagMatch[1];
  const contentStart = openTagMatch.index + openTagMatch[0].length;

  if (tagName.toLowerCase() === 'body') {
    const closeBody = html.slice(contentStart).search(/<\/body\s*>/i);
    if (closeBody === -1) {
      return html.slice(contentStart);
    }
    return html.slice(contentStart, contentStart + closeBody);
  }

  const openRegex = new RegExp(`<${tagName}(\\s[^>]*)?>`, 'gi');
  const closeRegex = new RegExp(`</${tagName}\\s*>`, 'gi');

  openRegex.lastIndex = contentStart;
  closeRegex.lastIndex = contentStart;

  let depth = 1;
  let cursor = contentStart;

  while (depth > 0 && cursor < html.length) {
    openRegex.lastIndex = cursor;
    closeRegex.lastIndex = cursor;

    const nextOpen = openRegex.exec(html);
    const nextClose = closeRegex.exec(html);

    if (!nextClose) {
      return null;
    }

    if (nextOpen && nextOpen.index < nextClose.index) {
      depth += 1;
      cursor = nextOpen.index + nextOpen[0].length;
      continue;
    }

    depth -= 1;

    if (depth === 0) {
      return html.slice(contentStart, nextClose.index);
    }

    cursor = nextClose.index + nextClose[0].length;
  }

  return null;
};

const extractBodyInnerHtml = (html: string): string | null => {
  const bodyOpen = html.match(/<body\b([^>]*)>/i);
  if (!bodyOpen || bodyOpen.index === undefined) {
    return null;
  }

  const contentStart = bodyOpen.index + bodyOpen[0].length;
  const closeBody = html.slice(contentStart).search(/<\/body\s*>/i);
  if (closeBody === -1) {
    return html.slice(contentStart);
  }
  return html.slice(contentStart, contentStart + closeBody);
};

const extractBodyClassName = (html: string): string | null => {
  const bodyOpen = html.match(/<body\b([^>]*)>/i);
  if (!bodyOpen?.[1]) {
    return null;
  }
  const classMatch = bodyOpen[1].match(/\bclass\s*=\s*(["'])(.*?)\1/i);
  return classMatch?.[2]?.trim() || null;
};

const extractHtmlAttribute = (
  html: string,
  attrName: string
): string | undefined => {
  const htmlOpen = html.match(/<html\b([^>]*)>/i);
  if (!htmlOpen?.[1]) return undefined;
  const match = htmlOpen[1].match(
    new RegExp(`\\b${attrName}\\s*=\\s*(["'])(.*?)\\1`, 'i')
  );
  return match?.[2]?.trim() || undefined;
};

const extractHtmlAttributes = (
  html: string
): WebflowExtractedPage['htmlAttributes'] => ({
  'data-wf-domain': extractHtmlAttribute(html, 'data-wf-domain'),
  'data-wf-page': extractHtmlAttribute(html, 'data-wf-page'),
  'data-wf-site': extractHtmlAttribute(html, 'data-wf-site'),
});

const extractTitle = (html: string): string | null => {
  const match = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  return match ? match[1].trim() : null;
};

const stripScriptsFromHtml = (html: string): string =>
  html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');

const isExecutableScriptType = (type: string | undefined): boolean => {
  if (!type) return true;
  const normalized = type.trim().toLowerCase();
  return (
    normalized === 'text/javascript' ||
    normalized === 'application/javascript' ||
    normalized === 'module' ||
    normalized === 'text/ecmascript' ||
    normalized === 'application/ecmascript'
  );
};

/**
 * Pull <script> tags from the published document in document order.
 * Content HTML keeps scripts stripped; these are re-injected via the DOM API
 * (innerHTML does not execute scripts).
 */
const extractScripts = (
  html: string,
  publishOrigin: string
): WebflowScriptAsset[] => {
  const scripts: WebflowScriptAsset[] = [];

  for (const match of html.matchAll(
    /<script\b([^>]*)>([\s\S]*?)<\/script>/gi
  )) {
    const attrs = match[1] || '';
    const content = (match[2] || '').trim();

    const typeMatch = attrs.match(/\btype\s*=\s*(["'])(.*?)\1/i);
    const type = typeMatch?.[2]?.trim();
    if (!isExecutableScriptType(type)) {
      continue;
    }

    const srcMatch = attrs.match(/\bsrc\s*=\s*(["'])(.*?)\1/i);
    const src = srcMatch?.[2]
      ? toAbsoluteUrl(srcMatch[2].trim(), publishOrigin)
      : undefined;

    if (!src && !content) {
      continue;
    }

    const idMatch = attrs.match(/\bid\s*=\s*(["'])(.*?)\1/i);
    const crossOriginMatch = attrs.match(
      /\bcrossorigin\s*=\s*(["'])(.*?)\1/i
    );
    const integrityMatch = attrs.match(/\bintegrity\s*=\s*(["'])(.*?)\1/i);
    const referrerPolicyMatch = attrs.match(
      /\breferrerpolicy\s*=\s*(["'])(.*?)\1/i
    );

    scripts.push({
      src,
      content: src ? undefined : content,
      type,
      async: /\basync\b/i.test(attrs),
      defer: /\bdefer\b/i.test(attrs),
      id: idMatch?.[2],
      crossOrigin:
        crossOriginMatch?.[2] ||
        (/\bcrossorigin\b/i.test(attrs) ? 'anonymous' : undefined),
      integrity: integrityMatch?.[2],
      referrerPolicy: referrerPolicyMatch?.[2],
      nomodule: /\bnomodule\b/i.test(attrs),
    });
  }

  return scripts;
};

const NAV_MARKER = 'data-tradewing-nav';

/**
 * Remove `#data-tradewing-nav` (or attr/class marker) from HTML so Tradewing
 * chrome mode does not show a duplicate Webflow navbar.
 */
const stripWebflowNavElement = (html: string): string => {
  const openTagMatch =
    html.match(
      new RegExp(
        `<([a-zA-Z][\\w:-]*)([^>]*\\sid\\s*=\\s*(["'])${NAV_MARKER}\\3[^>]*)>`,
        'i'
      )
    ) ||
    html.match(
      new RegExp(
        `<([a-zA-Z][\\w:-]*)([^>]*\\s${NAV_MARKER}(?:\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s>]*))?[^>]*)>`,
        'i'
      )
    ) ||
    html.match(
      new RegExp(
        `<([a-zA-Z][\\w:-]*)([^>]*\\sclass\\s*=\\s*(["'])(?:(?!\\3).)*\\b${NAV_MARKER}\\b(?:(?!\\3).)*\\3[^>]*)>`,
        'i'
      )
    );

  if (!openTagMatch || openTagMatch.index === undefined) {
    return html;
  }

  const tagName = openTagMatch[1];
  const start = openTagMatch.index;
  const contentStart = start + openTagMatch[0].length;
  const voidTags = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
  ]);

  if (voidTags.has(tagName.toLowerCase())) {
    return html.slice(0, start) + html.slice(contentStart);
  }

  const openRegex = new RegExp(`<${tagName}(\\s[^>]*)?>`, 'gi');
  const closeRegex = new RegExp(`</${tagName}\\s*>`, 'gi');
  openRegex.lastIndex = contentStart;
  closeRegex.lastIndex = contentStart;

  let depth = 1;
  let cursor = contentStart;

  while (depth > 0 && cursor < html.length) {
    openRegex.lastIndex = cursor;
    closeRegex.lastIndex = cursor;
    const nextOpen = openRegex.exec(html);
    const nextClose = closeRegex.exec(html);
    if (!nextClose) {
      return html;
    }
    if (nextOpen && nextOpen.index < nextClose.index) {
      depth += 1;
      cursor = nextOpen.index + nextOpen[0].length;
      continue;
    }
    depth -= 1;
    if (depth === 0) {
      const end = nextClose.index + nextClose[0].length;
      return html.slice(0, start) + html.slice(end);
    }
    cursor = nextClose.index + nextClose[0].length;
  }

  return html;
};

const isFontStylesheet = (href: string): boolean =>
  /fonts\.googleapis\.com|fonts\.gstatic\.com|use\.typekit\.net|fonts\.adobe\.com/i.test(
    href
  );

const extractAssets = (
  html: string,
  publishOrigin: string
): WebflowExtractedAssets => {
  const stylesheetHrefs: string[] = [];
  const inlineStyles: string[] = [];
  const fontLinks: WebflowExtractedAssets['fontLinks'] = [];

  for (const match of html.matchAll(/<link\b[^>]*>/gi)) {
    const tag = match[0];
    const hrefMatch = tag.match(/\bhref\s*=\s*(["'])(.*?)\1/i);
    const href = hrefMatch?.[2]
      ? toAbsoluteUrl(hrefMatch[2], publishOrigin)
      : '';

    if (!href) {
      continue;
    }

    const relMatch = tag.match(/\brel\s*=\s*(["'])(.*?)\1/i);
    const rel = (relMatch?.[2] || '').toLowerCase();

    if (rel.includes('stylesheet') || isFontStylesheet(href)) {
      stylesheetHrefs.push(href);
      continue;
    }

    if (
      rel.includes('preconnect') ||
      rel.includes('preload') ||
      rel.includes('dns-prefetch')
    ) {
      const crossOrigin = /\bcrossorigin\b/i.test(tag)
        ? tag.match(/\bcrossorigin\s*=\s*(["'])(.*?)\1/i)?.[2] || 'anonymous'
        : undefined;
      fontLinks.push({ rel, href, crossOrigin });
    }
  }

  for (const match of html.matchAll(/<style\b[^>]*>([\s\S]*?)<\/style>/gi)) {
    if (match[1]?.trim()) {
      inlineStyles.push(rewriteUrlsInCssOrHtml(match[1], publishOrigin));
    }
  }

  const scripts = extractScripts(html, publishOrigin);
  const scriptSrcs = scripts
    .map((script) => script.src)
    .filter((src): src is string => !!src);
  const inlineScripts = scripts
    .map((script) => script.content)
    .filter((content): content is string => !!content);

  return {
    stylesheetHrefs: [...new Set(stylesheetHrefs)],
    inlineStyles,
    scripts,
    scriptSrcs: [...new Set(scriptSrcs)],
    inlineScripts,
    fontLinks,
  };
};

const fetchStylesheetText = async (href: string): Promise<string | null> => {
  try {
    const response = await fetch(href, {
      headers: {
        Accept: 'text/css,*/*;q=0.1',
        'User-Agent': 'Tradewing-Webflow-Hybrid',
      },
      redirect: 'follow',
    });

    if (!response.ok) {
      return null;
    }

    return await response.text();
  } catch (error) {
    console.error('Failed to fetch Webflow stylesheet', { href, error });
    return null;
  }
};

/**
 * Fetch Webflow site CSS and scope it under the content wrapper so typography
 * and component styles win over Tradewing/Chakra global resets.
 */
export const prepareWebflowPageAssets = async (
  assets: WebflowExtractedAssets
): Promise<WebflowExtractedAssets> => {
  const inlineStyles = assets.inlineStyles.map((css) => scopeWebflowCss(css));
  const stylesheetHrefs: string[] = [];

  await Promise.all(
    assets.stylesheetHrefs.map(async (href) => {
      if (isFontStylesheet(href)) {
        stylesheetHrefs.push(href);
        return;
      }

      const cssText = await fetchStylesheetText(href);
      if (!cssText) {
        stylesheetHrefs.push(href);
        return;
      }

      const withAbsoluteUrls = rewriteUrlsInCssOrHtml(cssText, href);
      inlineStyles.push(scopeWebflowCss(withAbsoluteUrls));
    })
  );

  return {
    ...assets,
    stylesheetHrefs: [...new Set(stylesheetHrefs)],
    inlineStyles,
  };
};

/**
 * Extract a published Webflow page for static/marketing routes.
 * - `full`: entire <body> (Webflow nav + content + footer)
 * - `content`: [data-tradewing-content] region for Tradewing chrome mode
 */
export const extractWebflowContent = (
  html: string,
  publishOrigin: string,
  mode: 'full' | 'content' = 'full'
): WebflowExtractedPage | null => {
  const rawContent =
    mode === 'content'
      ? extractByAttribute(html) ?? extractBodyInnerHtml(html)
      : extractBodyInnerHtml(html) ?? extractByAttribute(html);

  if (!rawContent) {
    return null;
  }

  const withoutScripts = stripScriptsFromHtml(
    rewriteHtmlContentUrls(rawContent, publishOrigin)
  );
  // TRADEWING chrome already supplies Blueprint nav — drop Webflow nav if present.
  const contentHtml = promoteImgDimensionAttributes(
    mode === 'content' ? stripWebflowNavElement(withoutScripts) : withoutScripts
  );

  return {
    contentHtml,
    assets: extractAssets(html, publishOrigin),
    title: extractTitle(html),
    bodyClassName: extractBodyClassName(html),
    htmlAttributes: extractHtmlAttributes(html),
  };
};
